/**
 * Local SQLite store (plain TypeScript; the driver is injected).
 *
 *  - booths     cached booth list per assembly (GET /assemblies/:aid/booths)
 *  - sections   last known SERVER copy of each section (data + version)
 *  - drafts     raw, unvalidated form state, so nothing typed is lost if the app dies mid-form
 *  - outbox     writes waiting to reach the server (section PUTs and location POSTs), in order
 *  - kv         small settings (user, assembly, language)
 *
 * A local edit lives only in the outbox until the server accepts it; the sections table
 * always holds what the server last said. That keeps "discard" and "use server copy" trivial.
 *
 * Every write goes through one async lock, so a transaction (BEGIN … COMMIT on the single
 * connection) can never interleave with another write.
 */
import type { SectionName } from "@npac/rules";
import { Emitter, type Topic } from "./emitter";
import type { SqlDb, SqlValue } from "./sql";
import type {
  Booth,
  BoothLocation,
  LocationOutboxItem,
  LocationPayload,
  OutboxError,
  OutboxItem,
  OutboxStatus,
  SectionOutboxItem,
  SectionPayload,
  ServerSection,
} from "./types";

const SCHEMA_VERSION = 1;

const MIGRATIONS: Record<number, string> = {
  1: `
    CREATE TABLE IF NOT EXISTS kv (key TEXT PRIMARY KEY NOT NULL, value TEXT NOT NULL);
    CREATE TABLE IF NOT EXISTS booths (
      assembly_id TEXT NOT NULL,
      code        TEXT NOT NULL,
      no          INTEGER NOT NULL,
      aux         TEXT NOT NULL DEFAULT '',
      json        TEXT NOT NULL,
      PRIMARY KEY (assembly_id, code)
    );
    CREATE INDEX IF NOT EXISTS booths_order ON booths (assembly_id, no, aux);
    CREATE TABLE IF NOT EXISTS sections (
      assembly_id TEXT NOT NULL,
      booth_code  TEXT NOT NULL,
      section     TEXT NOT NULL,
      version     INTEGER NOT NULL,
      json        TEXT NOT NULL,
      fetched_at  INTEGER NOT NULL,
      -- outbox item id whose PUT produced this version (so an open form based on that item can rebase)
      ack_item_id INTEGER,
      PRIMARY KEY (assembly_id, booth_code, section)
    );
    CREATE TABLE IF NOT EXISTS drafts (
      assembly_id TEXT NOT NULL,
      booth_code  TEXT NOT NULL,
      section     TEXT NOT NULL,
      json        TEXT NOT NULL,
      updated_at  INTEGER NOT NULL,
      PRIMARY KEY (assembly_id, booth_code, section)
    );
    CREATE TABLE IF NOT EXISTS outbox (
      id              INTEGER PRIMARY KEY AUTOINCREMENT,
      kind            TEXT NOT NULL,
      assembly_id     TEXT NOT NULL,
      booth_code      TEXT NOT NULL,
      section         TEXT,
      payload         TEXT NOT NULL,
      base_version    INTEGER NOT NULL DEFAULT 0,
      force           INTEGER NOT NULL DEFAULT 0,
      device_id       TEXT NOT NULL,
      status          TEXT NOT NULL DEFAULT 'pending',
      attempts        INTEGER NOT NULL DEFAULT 0,
      next_attempt_at INTEGER NOT NULL DEFAULT 0,
      last_error      TEXT,
      server_copy     TEXT,
      rev             INTEGER NOT NULL DEFAULT 1,
      created_at      INTEGER NOT NULL,
      updated_at      INTEGER NOT NULL
    );
    CREATE UNIQUE INDEX IF NOT EXISTS outbox_one_per_section
      ON outbox (assembly_id, booth_code, section) WHERE kind = 'section';
  `,
};

interface OutboxRow {
  id: number;
  kind: string;
  assembly_id: string;
  booth_code: string;
  section: string | null;
  payload: string;
  base_version: number;
  force: number;
  device_id: string;
  status: string;
  attempts: number;
  next_attempt_at: number;
  last_error: string | null;
  server_copy: string | null;
  rev: number;
  created_at: number;
  updated_at: number;
}

function parseJson<T>(s: string | null | undefined, fallback: T): T {
  if (s == null) return fallback;
  try {
    return JSON.parse(s) as T;
  } catch {
    return fallback;
  }
}

function rowToItem(r: OutboxRow): OutboxItem {
  const base = {
    id: Number(r.id),
    assemblyId: r.assembly_id,
    boothCode: r.booth_code,
    deviceId: r.device_id,
    status: r.status as OutboxStatus,
    attempts: Number(r.attempts),
    nextAttemptAt: Number(r.next_attempt_at),
    lastError: parseJson<OutboxError | null>(r.last_error, null),
    rev: Number(r.rev),
    createdAt: Number(r.created_at),
    updatedAt: Number(r.updated_at),
  };
  if (r.kind === "location") {
    const item: LocationOutboxItem = {
      ...base,
      kind: "location",
      section: null,
      payload: parseJson<LocationPayload>(r.payload, { lat: 0, lng: 0, accuracyM: null, capturedAt: "" }),
      baseVersion: 0,
      force: false,
      serverCopy: null,
    };
    return item;
  }
  const item: SectionOutboxItem = {
    ...base,
    kind: "section",
    section: r.section as SectionName,
    payload: parseJson<SectionPayload>(r.payload, { data: null, status: "partial", issues: [] }),
    baseVersion: Number(r.base_version),
    force: Number(r.force) === 1,
    serverCopy: parseJson<ServerSection | null>(r.server_copy, null),
  };
  return item;
}

class Mutex {
  private tail: Promise<void> = Promise.resolve();
  run<T>(fn: () => Promise<T>): Promise<T> {
    const p = this.tail.then(fn, fn);
    this.tail = p.then(
      () => undefined,
      () => undefined
    );
    return p;
  }
}

export interface QueueSectionInput {
  assemblyId: string;
  boothCode: string;
  section: SectionName;
  payload: SectionPayload;
  /** server version the user was looking at when editing (0 = new section) */
  baseVersion: number;
  deviceId: string;
  /**
   * The outbox item the form was loaded from, if any. If that item has reached the server
   * since, the new edit is based on the version the server returned for it.
   */
  basedOnItemId?: number | null;
}

export interface QueueLocationInput {
  assemblyId: string;
  boothCode: string;
  payload: LocationPayload;
  deviceId: string;
}

export interface Draft<T = unknown> {
  form: T;
  baseVersion: number;
  basedOnItemId: number | null;
  savedAt: number;
}

export interface OutboxCounts {
  pending: number;
  failed: number;
  conflict: number;
  total: number;
}

export class LocalStore {
  /** fires after every write */
  readonly changes = new Emitter();
  private lock = new Mutex();

  constructor(private db: SqlDb, private now: () => number = Date.now) {}

  private write<T>(topics: Topic | Topic[], fn: () => Promise<T>): Promise<T> {
    return this.lock.run(async () => {
      const out = await fn();
      for (const t of Array.isArray(topics) ? topics : [topics]) this.changes.emit(t);
      return out;
    });
  }

  async init(): Promise<void> {
    await this.lock.run(async () => {
      const row = await this.db.first<{ user_version: number }>("PRAGMA user_version");
      let v = Number(row?.user_version ?? 0);
      while (v < SCHEMA_VERSION) {
        v += 1;
        await this.db.exec(MIGRATIONS[v]);
        await this.db.exec(`PRAGMA user_version = ${v}`);
      }
    });
  }

  /* ---------------- kv ---------------- */

  async getKv(key: string): Promise<string | null> {
    const r = await this.db.first<{ value: string }>("SELECT value FROM kv WHERE key = ?", [key]);
    return r ? r.value : null;
  }

  setKv(key: string, value: string): Promise<void> {
    return this.write("kv", async () => {
      await this.db.run("INSERT INTO kv (key, value) VALUES (?, ?) ON CONFLICT(key) DO UPDATE SET value = excluded.value", [key, value]);
    });
  }

  deleteKv(key: string): Promise<void> {
    return this.write("kv", async () => {
      await this.db.run("DELETE FROM kv WHERE key = ?", [key]);
    });
  }

  /* ---------------- booths ---------------- */

  /** Replace the cached booth list of one assembly with a fresh download. */
  replaceBooths(assemblyId: string, booths: Booth[]): Promise<void> {
    return this.write("booths", async () => {
      await this.db.transaction(async (tx) => {
        await tx.run("DELETE FROM booths WHERE assembly_id = ?", [assemblyId]);
        for (const b of booths) {
          await tx.run("INSERT OR REPLACE INTO booths (assembly_id, code, no, aux, json) VALUES (?, ?, ?, ?, ?)", [
            assemblyId,
            b.code,
            b.no,
            b.aux ?? "",
            JSON.stringify(b),
          ]);
        }
        await tx.run("INSERT INTO kv (key, value) VALUES (?, ?) ON CONFLICT(key) DO UPDATE SET value = excluded.value", [
          `boothsFetchedAt:${assemblyId}`,
          String(this.now()),
        ]);
      });
    });
  }

  async listBooths(assemblyId: string): Promise<Booth[]> {
    const rows = await this.db.all<{ json: string }>("SELECT json FROM booths WHERE assembly_id = ? ORDER BY no, aux", [assemblyId]);
    return rows.map((r) => JSON.parse(r.json) as Booth);
  }

  async getBooth(assemblyId: string, code: string): Promise<Booth | null> {
    const r = await this.db.first<{ json: string }>("SELECT json FROM booths WHERE assembly_id = ? AND code = ?", [assemblyId, code]);
    return r ? (JSON.parse(r.json) as Booth) : null;
  }

  async boothsFetchedAt(assemblyId: string): Promise<number | null> {
    const v = await this.getKv(`boothsFetchedAt:${assemblyId}`);
    return v ? Number(v) : null;
  }

  /** Store a booth + all its sections from GET /assemblies/:aid/booths/:code. */
  putBoothDetail(assemblyId: string, booth: Booth, sections: Partial<Record<SectionName, ServerSection>>): Promise<void> {
    return this.write(["booths", "sections"], async () => {
      await this.db.transaction(async (tx) => {
        await tx.run("INSERT OR REPLACE INTO booths (assembly_id, code, no, aux, json) VALUES (?, ?, ?, ?, ?)", [
          assemblyId,
          booth.code,
          booth.no,
          booth.aux ?? "",
          JSON.stringify(booth),
        ]);
        for (const [name, s] of Object.entries(sections)) {
          if (s) await this.upsertServerSection(tx, assemblyId, booth.code, name as SectionName, s);
        }
      });
    });
  }

  /* ---------------- server copies of sections ---------------- */

  async getServerSection(assemblyId: string, code: string, section: SectionName): Promise<ServerSection | null> {
    const r = await this.db.first<{ json: string }>(
      "SELECT json FROM sections WHERE assembly_id = ? AND booth_code = ? AND section = ?",
      [assemblyId, code, section]
    );
    return r ? (JSON.parse(r.json) as ServerSection) : null;
  }

  async getServerSections(assemblyId: string, code: string): Promise<Partial<Record<SectionName, ServerSection>>> {
    const rows = await this.db.all<{ section: string; json: string }>(
      "SELECT section, json FROM sections WHERE assembly_id = ? AND booth_code = ?",
      [assemblyId, code]
    );
    const out: Partial<Record<SectionName, ServerSection>> = {};
    for (const r of rows) out[r.section as SectionName] = JSON.parse(r.json) as ServerSection;
    return out;
  }

  putServerSection(assemblyId: string, code: string, section: SectionName, s: ServerSection): Promise<void> {
    return this.write(["booths", "sections"], () => this.db.transaction((tx) => this.upsertServerSection(tx, assemblyId, code, section, s)));
  }

  /**
   * Keeps the newest server copy (never goes back to an older version) and patches the booth's
   * status summary. `ackItemId` = the outbox item whose PUT produced this version.
   */
  private async upsertServerSection(
    tx: SqlDb,
    assemblyId: string,
    code: string,
    section: SectionName,
    s: ServerSection,
    ackItemId: number | null = null
  ): Promise<void> {
    await tx.run(
      `INSERT INTO sections (assembly_id, booth_code, section, version, json, fetched_at, ack_item_id) VALUES (?, ?, ?, ?, ?, ?, ?)
       ON CONFLICT(assembly_id, booth_code, section) DO UPDATE SET version = excluded.version, json = excluded.json, fetched_at = excluded.fetched_at,
         ack_item_id = CASE WHEN excluded.ack_item_id IS NOT NULL THEN excluded.ack_item_id
                            WHEN excluded.version = sections.version THEN sections.ack_item_id ELSE NULL END
       WHERE excluded.version >= sections.version`,
      [assemblyId, code, section, s.version, JSON.stringify(s), this.now(), ackItemId]
    );
    const b = await tx.first<{ json: string }>("SELECT json FROM booths WHERE assembly_id = ? AND code = ?", [assemblyId, code]);
    if (b) {
      const booth = JSON.parse(b.json) as Booth;
      const cur = booth.sections?.[section];
      if (!cur || cur.version <= s.version) {
        const people = (s.data as { people?: unknown[] } | null)?.people;
        const count = s.counts?.people ?? (Array.isArray(people) ? people.length : cur?.count);
        booth.sections = { ...(booth.sections ?? {}), [section]: { status: s.status, version: s.version, updatedAt: s.updatedAt ?? "", ...(count != null ? { count } : {}) } };
        await tx.run("UPDATE booths SET json = ? WHERE assembly_id = ? AND code = ?", [JSON.stringify(booth), assemblyId, code]);
      }
    }
  }

  /* ---------------- drafts ---------------- */

  async getDraft<T>(assemblyId: string, code: string, section: SectionName): Promise<Draft<T> | null> {
    const r = await this.db.first<{ json: string }>(
      "SELECT json FROM drafts WHERE assembly_id = ? AND booth_code = ? AND section = ?",
      [assemblyId, code, section]
    );
    return r ? parseJson<Draft<T> | null>(r.json, null) : null;
  }

  saveDraft<T>(assemblyId: string, code: string, section: SectionName, form: T, baseVersion: number, basedOnItemId: number | null = null): Promise<void> {
    const draft: Draft<T> = { form, baseVersion, basedOnItemId, savedAt: this.now() };
    return this.write("drafts", async () => {
      await this.db.run(
        `INSERT INTO drafts (assembly_id, booth_code, section, json, updated_at) VALUES (?, ?, ?, ?, ?)
         ON CONFLICT(assembly_id, booth_code, section) DO UPDATE SET json = excluded.json, updated_at = excluded.updated_at`,
        [assemblyId, code, section, JSON.stringify(draft), draft.savedAt]
      );
    });
  }

  deleteDraft(assemblyId: string, code: string, section: SectionName): Promise<void> {
    return this.write("drafts", async () => {
      await this.db.run("DELETE FROM drafts WHERE assembly_id = ? AND booth_code = ? AND section = ?", [assemblyId, code, section]);
    });
  }

  async draftSections(assemblyId: string, code: string): Promise<SectionName[]> {
    const rows = await this.db.all<{ section: string }>("SELECT section FROM drafts WHERE assembly_id = ? AND booth_code = ?", [assemblyId, code]);
    return rows.map((r) => r.section as SectionName);
  }

  /* ---------------- outbox ---------------- */

  /**
   * Queue a validated section save. One outbox entry per booth+section: a second save
   * before the first one was sent replaces the payload in place (keeps its position,
   * its baseVersion and, for a conflict, the server copy the user still has to decide on).
   */
  queueSection(input: QueueSectionInput): Promise<number> {
    return this.write("outbox", async () => {
      const t = this.now();
      const existing = await this.db.first<OutboxRow>(
        "SELECT * FROM outbox WHERE kind = 'section' AND assembly_id = ? AND booth_code = ? AND section = ?",
        [input.assemblyId, input.boothCode, input.section]
      );
      if (existing) {
        const status: OutboxStatus = existing.status === "conflict" ? "conflict" : "pending";
        await this.db.run(
          `UPDATE outbox SET payload = ?, status = ?, attempts = 0, next_attempt_at = 0, last_error = NULL,
             device_id = ?, rev = rev + 1, updated_at = ? WHERE id = ?`,
          [JSON.stringify(input.payload), status, input.deviceId, t, existing.id]
        );
        return Number(existing.id);
      }
      let baseVersion = input.baseVersion;
      if (input.basedOnItemId != null) {
        const ack = await this.db.first<{ version: number; ack_item_id: number | null }>(
          "SELECT version, ack_item_id FROM sections WHERE assembly_id = ? AND booth_code = ? AND section = ?",
          [input.assemblyId, input.boothCode, input.section]
        );
        if (ack && ack.ack_item_id != null && Number(ack.ack_item_id) === input.basedOnItemId) baseVersion = Number(ack.version);
      }
      const r = await this.db.run(
        `INSERT INTO outbox (kind, assembly_id, booth_code, section, payload, base_version, force, device_id, status, created_at, updated_at)
         VALUES ('section', ?, ?, ?, ?, ?, 0, ?, 'pending', ?, ?)`,
        [input.assemblyId, input.boothCode, input.section, JSON.stringify(input.payload), baseVersion, input.deviceId, t, t]
      );
      return Number(r.lastInsertRowId);
    });
  }

  /** Every location share is kept (the server keeps a history too), in order. */
  queueLocation(input: QueueLocationInput): Promise<number> {
    return this.write("outbox", async () => {
      const t = this.now();
      const r = await this.db.run(
        `INSERT INTO outbox (kind, assembly_id, booth_code, section, payload, device_id, status, created_at, updated_at)
         VALUES ('location', ?, ?, NULL, ?, ?, 'pending', ?, ?)`,
        [input.assemblyId, input.boothCode, JSON.stringify(input.payload), input.deviceId, t, t]
      );
      return Number(r.lastInsertRowId);
    });
  }

  async listOutbox(): Promise<OutboxItem[]> {
    const rows = await this.db.all<OutboxRow>("SELECT * FROM outbox ORDER BY id");
    return rows.map(rowToItem);
  }

  async outboxForBooth(assemblyId: string, code: string): Promise<OutboxItem[]> {
    const rows = await this.db.all<OutboxRow>("SELECT * FROM outbox WHERE assembly_id = ? AND booth_code = ? ORDER BY id", [assemblyId, code]);
    return rows.map(rowToItem);
  }

  async outboxForAssembly(assemblyId: string): Promise<OutboxItem[]> {
    const rows = await this.db.all<OutboxRow>("SELECT * FROM outbox WHERE assembly_id = ? ORDER BY id", [assemblyId]);
    return rows.map(rowToItem);
  }

  async getOutboxItem(id: number): Promise<OutboxItem | null> {
    const r = await this.db.first<OutboxRow>("SELECT * FROM outbox WHERE id = ?", [id]);
    return r ? rowToItem(r) : null;
  }

  async findSectionItem(assemblyId: string, code: string, section: SectionName): Promise<SectionOutboxItem | null> {
    const r = await this.db.first<OutboxRow>(
      "SELECT * FROM outbox WHERE kind = 'section' AND assembly_id = ? AND booth_code = ? AND section = ?",
      [assemblyId, code, section]
    );
    return r ? (rowToItem(r) as SectionOutboxItem) : null;
  }

  /**
   * Next pending item after `afterId` (queue order) that is due by `dueBy`.
   * Location shares of one booth go strictly in order: a newer share waits while an older one
   * for the same booth is still pending (otherwise a retried old point could overwrite a newer one).
   */
  async nextPending(afterId: number, dueBy: number): Promise<OutboxItem | null> {
    const r = await this.db.first<OutboxRow>(
      `SELECT * FROM outbox o WHERE o.status = 'pending' AND o.id > ? AND o.next_attempt_at <= ?
         AND NOT (o.kind = 'location' AND EXISTS (
           SELECT 1 FROM outbox p WHERE p.kind = 'location' AND p.status = 'pending'
              AND p.assembly_id = o.assembly_id AND p.booth_code = o.booth_code AND p.id < o.id))
       ORDER BY o.id LIMIT 1`,
      [afterId, dueBy]
    );
    return r ? rowToItem(r) : null;
  }

  async counts(): Promise<OutboxCounts> {
    const rows = await this.db.all<{ status: string; n: number }>("SELECT status, COUNT(*) AS n FROM outbox GROUP BY status");
    const c: OutboxCounts = { pending: 0, failed: 0, conflict: 0, total: 0 };
    for (const r of rows) {
      const n = Number(r.n);
      if (r.status === "pending" || r.status === "failed" || r.status === "conflict") c[r.status] = n;
      c.total += n;
    }
    return c;
  }

  /**
   * The server accepted a section PUT. If the user edited the section again while the
   * request was in flight (rev changed), keep the newer edit queued, now based on the
   * version the server just returned.
   */
  completeSection(id: number, sentRev: number, assemblyId: string, code: string, section: SectionName, saved: ServerSection): Promise<"done" | "requeued"> {
    return this.write(["outbox", "sections", "booths"], async () => {
      let result: "done" | "requeued" = "done";
      await this.db.transaction(async (tx) => {
        await this.upsertServerSection(tx, assemblyId, code, section, saved, id);
        const del = await tx.run("DELETE FROM outbox WHERE id = ? AND rev = ?", [id, sentRev]);
        if (del.changes === 0) {
          result = "requeued";
          await tx.run(
            `UPDATE outbox SET base_version = ?, force = 0, server_copy = NULL, attempts = 0, next_attempt_at = 0, last_error = NULL, updated_at = ?
             WHERE id = ?`,
            [saved.version, this.now(), id]
          );
        }
      });
      return result;
    });
  }

  completeLocation(id: number, assemblyId: string, code: string, location: BoothLocation): Promise<void> {
    return this.write(["outbox", "booths"], async () => {
      await this.db.transaction(async (tx) => {
        await tx.run("DELETE FROM outbox WHERE id = ?", [id]);
        const b = await tx.first<{ json: string }>("SELECT json FROM booths WHERE assembly_id = ? AND code = ?", [assemblyId, code]);
        if (b) {
          const booth = JSON.parse(b.json) as Booth;
          booth.location = location;
          await tx.run("UPDATE booths SET json = ? WHERE assembly_id = ? AND code = ?", [JSON.stringify(booth), assemblyId, code]);
        }
      });
    });
  }

  /**
   * 409: keep the local edit, remember the server copy, wait for the user to choose.
   * The server copy is also the freshest known server state, so it updates the cache.
   */
  markConflict(id: number, current: ServerSection): Promise<void> {
    return this.write(["outbox", "sections", "booths"], async () => {
      await this.db.transaction(async (tx) => {
        const r = await tx.first<OutboxRow>("SELECT * FROM outbox WHERE id = ? AND kind = 'section'", [id]);
        if (!r) return;
        await tx.run(
          `UPDATE outbox SET status = 'conflict', server_copy = ?, attempts = attempts + 1, next_attempt_at = 0,
             last_error = ?, updated_at = ? WHERE id = ?`,
          [
            JSON.stringify(current),
            JSON.stringify({ code: "conflict", status: 409, message: "Changed on the server since you loaded it" } satisfies OutboxError),
            this.now(),
            id,
          ]
        );
        await this.upsertServerSection(tx, r.assembly_id, r.booth_code, r.section as SectionName, current);
      });
    });
  }

  /**
   * Permanent failure (400 / 403 / 404 ...). Only applies if the item was not edited
   * meanwhile; a newer edit stays pending and gets its own chance.
   */
  markFailed(id: number, sentRev: number, error: OutboxError): Promise<void> {
    return this.write("outbox", async () => {
      await this.db.run(
        `UPDATE outbox SET status = 'failed', attempts = attempts + 1, last_error = ?, updated_at = ? WHERE id = ? AND rev = ?`,
        [JSON.stringify(error), this.now(), id, sentRev]
      );
    });
  }

  /** Temporary failure: keep the item pending, remember why, optionally wait until `nextAttemptAt`. */
  recordAttempt(id: number, error: OutboxError, nextAttemptAt: number): Promise<void> {
    return this.write("outbox", async () => {
      await this.db.run(
        `UPDATE outbox SET attempts = attempts + 1, last_error = ?, next_attempt_at = ?, updated_at = ? WHERE id = ? AND status = 'pending'`,
        [JSON.stringify(error), nextAttemptAt, this.now(), id]
      );
    });
  }

  /** Conflict → "keep mine": resend with force: true. */
  keepMine(id: number): Promise<void> {
    return this.write("outbox", async () => {
      const r = await this.db.first<OutboxRow>("SELECT * FROM outbox WHERE id = ? AND kind = 'section'", [id]);
      if (!r) return;
      const server = parseJson<ServerSection | null>(r.server_copy, null);
      await this.db.run(
        `UPDATE outbox SET status = 'pending', force = 1, base_version = ?, attempts = 0, next_attempt_at = 0,
           last_error = NULL, rev = rev + 1, updated_at = ? WHERE id = ?`,
        [server ? server.version : Number(r.base_version), this.now(), id]
      );
    });
  }

  /** Conflict → "use server copy": drop the local edit (and its draft), keep the server's data. */
  useServerCopy(id: number): Promise<void> {
    return this.write(["outbox", "sections", "booths", "drafts"], async () => {
      const r = await this.db.first<OutboxRow>("SELECT * FROM outbox WHERE id = ? AND kind = 'section'", [id]);
      if (!r) return;
      const server = parseJson<ServerSection | null>(r.server_copy, null);
      await this.db.transaction(async (tx) => {
        if (server) await this.upsertServerSection(tx, r.assembly_id, r.booth_code, r.section as SectionName, server);
        await tx.run("DELETE FROM drafts WHERE assembly_id = ? AND booth_code = ? AND section = ?", [r.assembly_id, r.booth_code, r.section as SqlValue]);
        await tx.run("DELETE FROM outbox WHERE id = ?", [id]);
      });
    });
  }

  /** Failed item → try again (e.g. after an admin assigned the booth). */
  retry(id: number): Promise<void> {
    return this.write("outbox", async () => {
      await this.db.run(
        `UPDATE outbox SET status = 'pending', next_attempt_at = 0, last_error = NULL, rev = rev + 1, updated_at = ? WHERE id = ? AND status = 'failed'`,
        [this.now(), id]
      );
    });
  }

  /** Drop an item the user no longer wants sent. */
  discard(id: number): Promise<void> {
    return this.write("outbox", async () => {
      await this.db.run("DELETE FROM outbox WHERE id = ?", [id]);
    });
  }
}
